/* v8 Path F — shared scenario signature / diff utilities.
   Extracted from ProfileManager so ScenarioStatus (topbar dirty indicator)
   and the diff popover can reuse the exact same "what counts as dirty"
   logic. One source of truth prevents drift between "profile Save button
   says dirty" vs "topbar says clean". */
import { formatPlainNumber } from './formatting.js';

/* Fields we intentionally exclude from the dirty comparison:
     - profiles, activeProfile — meta, not scenario values
     - sectionDensity           — UI density preferences
     - name, description        — profile labels
     - savedAt, basedOn, id     — profile metadata
     - _txCacheReady, _ledgerSource, _ledgerReady — app-runtime flags
     - zbBannerHidden           — session UI ack
*/
const META_FIELDS = new Set([
  'profiles', 'activeProfile', 'sectionDensity',
  'name', 'description', 'savedAt', 'basedOn', 'id',
  '_txCacheReady', '_ledgerSource', '_ledgerReady',
  'zbBannerHidden',
]);

function stripMeta(obj) {
  const out = {};
  for (const k of Object.keys(obj || {})) {
    if (META_FIELDS.has(k)) continue;
    out[k] = obj[k];
  }
  return out;
}

export function stableStringify(obj) {
  return JSON.stringify(obj, (_k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc; }, {});
    }
    return v;
  });
}

export function signatureOfProfile(profile) {
  if (!profile) return null;
  return stableStringify(stripMeta(profile));
}

export function signatureOfScenario(scenario) {
  return stableStringify(stripMeta(scenario));
}

/* Shallow diff of two scenario payloads (post-strip). Returns a list of
   { path, a, b, summary } objects — one per changed top-level key. For
   nested object changes the whole object is reported as one entry; for
   arrays we report length + first/last-item preview. Good enough for a
   topbar "what changed" popover without a full diff library. */
export function diffScenarios(from, to) {
  const fromS = stripMeta(from || {});
  const toS   = stripMeta(to   || {});
  const keys = new Set([...Object.keys(fromS), ...Object.keys(toS)]);
  const changes = [];
  for (const k of keys) {
    const a = fromS[k];
    const b = toS[k];
    const aJ = stableStringify(a);
    const bJ = stableStringify(b);
    if (aJ === bJ) continue;
    changes.push({
      path: k,
      a, b,
      summary: summarizeChange(k, a, b),
    });
  }
  return changes.sort((x, y) => x.path.localeCompare(y.path));
}

function summarizeChange(key, a, b) {
  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
  const isArr = Array.isArray;
  if (isNum(a) && isNum(b)) {
    const delta = b - a;
    const sign = delta >= 0 ? '+' : '';
    return `${fmtNum(a)} → ${fmtNum(b)} (${sign}${fmtNum(delta)})`;
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    return `${a ? 'ON' : 'OFF'} → ${b ? 'ON' : 'OFF'}`;
  }
  if (isArr(a) && isArr(b)) {
    if (a.length !== b.length) return `${a.length} → ${b.length} items`;
    return `${a.length} item${a.length === 1 ? '' : 's'} (contents changed)`;
  }
  if (a == null && b != null) return `— → set`;
  if (b == null && a != null) return `set → —`;
  if (typeof a === 'object' && typeof b === 'object') {
    return `object updated`;
  }
  return `${JSON.stringify(a)} → ${JSON.stringify(b)}`;
}

function fmtNum(n) {
  if (!Number.isFinite(n)) return String(n);
  if (Math.abs(n) < 1 && n !== 0) return n.toFixed(4);
  return formatPlainNumber(n, { decimals: 2, scale: false });
}
